/**
 * 🔷 HEX GRID UTILITIES
 *
 * Shared utilities for hex grid operations.
 * Uses axial coordinates (q, r) for hex grids.
 *
 * Reference: https://www.redblobgames.com/grids/hexagons/
 */

/**
 * The 6 adjacent directions in axial coordinates (pointy-top hexagons)
 */
export const HEX_DIRECTIONS = [
  { q: 1, r: 0 },    // East
  { q: 1, r: -1 },   // Northeast
  { q: 0, r: -1 },   // Northwest
  { q: -1, r: 0 },   // West
  { q: -1, r: 1 },   // Southwest
  { q: 0, r: 1 },    // Southeast
];

/**
 * Convert axial coordinates to a node ID string
 * @param {number} q - Q coordinate
 * @param {number} r - R coordinate
 * @returns {string} Node ID like "node_1_-2"
 */
export const coordsToId = (q, r) => `node_${q}_${r}`;

/**
 * Convert a node ID string back to axial coordinates
 * @param {string} nodeId - Node ID like "node_1_-2" or "kingdom"
 * @returns {{q: number, r: number}|null} Coordinates or null if invalid
 */
export const idToCoords = (nodeId) => {
  if (nodeId === 'kingdom') return { q: 0, r: 0 };
  const match = nodeId.match(/node_(-?\d+)_(-?\d+)/);
  if (!match) return null;
  return { q: parseInt(match[1]), r: parseInt(match[2]) };
};

/**
 * Get all 6 adjacent coordinates for a given hex
 * @param {number} q - Q coordinate
 * @param {number} r - R coordinate
 * @returns {Array<{q: number, r: number}>} Array of 6 adjacent coordinates
 */
export const getAdjacentCoords = (q, r) =>
  HEX_DIRECTIONS.map(dir => ({ q: q + dir.q, r: r + dir.r }));

/**
 * Convert axial coordinates to pixel position (pointy-top hexagons)
 * @param {number} q - Q coordinate
 * @param {number} r - R coordinate
 * @param {number} size - Hex size (distance from center to corner)
 * @returns {{x: number, y: number}} Pixel position
 */
export const hexToPixel = (q, r, size) => ({
  x: Math.round(size * Math.sqrt(3) * (q + r / 2)),
  y: Math.round(size * (3 / 2) * r),
});

/**
 * Convert pixel position to axial coordinates (with rounding)
 * @param {number} x - Pixel X
 * @param {number} y - Pixel Y
 * @param {number} size - Hex size
 * @returns {{q: number, r: number}} Axial coordinates
 */
export const pixelToHex = (x, y, size) => {
  const q = (Math.sqrt(3) / 3 * x - 1 / 3 * y) / size;
  const r = (2 / 3 * y) / size;

  // Round to nearest hex using cube coordinates
  let rx = Math.round(q);
  let rz = Math.round(r);
  let ry = Math.round(-q - r);

  const xDiff = Math.abs(rx - q);
  const yDiff = Math.abs(ry - (-q - r));
  const zDiff = Math.abs(rz - r);

  if (xDiff > yDiff && xDiff > zDiff) {
    rx = -ry - rz;
  } else if (yDiff > zDiff) {
    // ry = -rx - rz; (not needed for axial)
  } else {
    rz = -rx - ry;
  }

  return { q: rx, r: rz };
};

/**
 * Calculate hex distance between two hexes
 * @param {number} q1 - First hex Q
 * @param {number} r1 - First hex R
 * @param {number} q2 - Second hex Q
 * @param {number} r2 - Second hex R
 * @returns {number} Distance in hex steps
 */
export const hexDistance = (q1, r1, q2, r2) => {
  return Math.max(
    Math.abs(q1 - q2),
    Math.abs(r1 - r2),
    Math.abs((q1 + r1) - (q2 + r2))
  );
};

/**
 * Get hex corner points for drawing (pointy-top)
 * @param {number} size - Hex size
 * @returns {number[]} Flat array of [x1, y1, x2, y2, ...] corner points
 */
export const getHexPoints = (size) => {
  const points = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 30);
    points.push(size * Math.cos(angle), size * Math.sin(angle));
  }
  return points;
};

/**
 * Check if two hex coordinates are the same
 * @param {{q: number, r: number}} a - First hex
 * @param {{q: number, r: number}} b - Second hex
 * @returns {boolean} True if same hex
 */
export const sameHex = (a, b) => a.q === b.q && a.r === b.r;

/**
 * Check if two hexes are adjacent
 * @param {number} q1 - First hex Q
 * @param {number} r1 - First hex R
 * @param {number} q2 - Second hex Q
 * @param {number} r2 - Second hex R
 * @returns {boolean} True if adjacent
 */
export const areAdjacent = (q1, r1, q2, r2) => hexDistance(q1, r1, q2, r2) === 1;

/**
 * Check if 3 hexes form a valid triangle (all mutually adjacent)
 * @param {Array<{q: number, r: number}>} coords - Array of 3 hex coordinates
 * @returns {boolean} True if all 3 hexes are mutually adjacent
 */
export const isValidTriangle = (coords) => {
  if (!coords || coords.length !== 3) return false;
  const [a, b, c] = coords;
  return (
    areAdjacent(a.q, a.r, b.q, b.r) &&
    areAdjacent(b.q, b.r, c.q, c.r) &&
    areAdjacent(c.q, c.r, a.q, a.r)
  );
};

/**
 * Get the center pixel position of a triangle of 3 hexes
 * @param {Array<{q: number, r: number}>} coords - Array of 3 hex coordinates
 * @param {number} size - Hex size
 * @returns {{x: number, y: number}} Center pixel position
 */
export const getTriangleCenter = (coords, size) => {
  if (!coords || coords.length !== 3) return { x: 0, y: 0 };

  const positions = coords.map(c => hexToPixel(c.q, c.r, size));
  return {
    x: (positions[0].x + positions[1].x + positions[2].x) / 3,
    y: (positions[0].y + positions[1].y + positions[2].y) / 3,
  };
};
